import { useEffect, useRef } from "react";
import { Timeout } from "src/types/time";
import { StoreApi } from "zustand";
import { VirtualListStore } from "../stores/virtualListStore";

const useCalculateCells = (store: StoreApi<VirtualListStore>) => {
	const timeout = useRef<Timeout | null>(null);
	const didInit = useRef(false);
	const prevScrollPosition = useRef<number>(0);
	useEffect(() => {
		const calculateCells = ({
			mode = "RenderAndClean",
			marginPercentage = 0,
			topMargin = 0,
			bottomMargin = 0,
		}: {
			mode?: "RenderAndClean" | "RenderOnly" | "CleanOnly";
			marginPercentage?: number;
			topMargin?: number;
			bottomMargin?: number;
		} = {}) => {
			const state = store.getState();
			const { scrollPosition, windowHeight, topOffset } = state;
			const topBound =
				topOffset + scrollPosition - marginPercentage * windowHeight - topMargin;
			const bottomBound =
				topOffset +
				scrollPosition +
				windowHeight +
				marginPercentage * windowHeight +
				bottomMargin;

			let hasDiff = false;
			// Using a setter object so that we can call the function by reference
			// Means we only have to check for difference once
			// Yes this is an extremely intense performance optimization but this runs very, very frequently
			const setter = {
				setCell: (key: string, isCell: boolean) => {
					if (Boolean(state.isCellLookup[key]?.isCell) !== isCell) {
						hasDiff = true;
						state.isCellLookup[key] = { isCell };
						setter.setCell = (key: string, isCell: boolean) => {
							state.isCellLookup[key] = { isCell };
						};
					}
				},
			};

			switch (mode) {
				case "RenderAndClean":
					for (const [key, measurement] of Object.entries(state.measurementLookup)) {
						const { y, height } = measurement;
						if (y + height >= topBound && y <= bottomBound) {
							setter.setCell(key, true);
						} else {
							setter.setCell(key, false);
						}
					}
					break;
				case "RenderOnly":
					for (const [key, measurement] of Object.entries(state.measurementLookup)) {
						const { y, height } = measurement;
						if (y + height >= topBound && y <= bottomBound) {
							setter.setCell(key, true);
						}
					}
					break;
				case "CleanOnly":
					for (const [key, measurement] of Object.entries(state.measurementLookup)) {
						const { y, height } = measurement;
						if (!(y + height >= topBound && y <= bottomBound)) {
							setter.setCell(key, false);
						}
					}
			}

			if (hasDiff) {
				store.setState({
					isCellLookup: {
						...state.isCellLookup,
					},
				});
			}
		};
		calculateCells({
			mode: "RenderOnly",
			// Rendering very little in order to render faster
			marginPercentage: 0.2,
		});
		const unsubscribe = store.subscribe((state, prevState) => {
			if (
				state.scrollPosition === prevState.scrollPosition &&
				state.topOffset === prevState.topOffset &&
				state.windowHeight === prevState.windowHeight &&
				state.measurementLookup === prevState.measurementLookup
			) {
				return;
			}
			if (!didInit.current) {
				if (Object.keys(state.measurementLookup).length === 0) return;
				didInit.current = true;
				calculateCells({
					mode: "RenderOnly",
					// Rendering very little in order to render faster
					marginPercentage: 0.2,
				});
				if (timeout.current == null) {
					timeout.current = setTimeout(() => {
						timeout.current = null;
						calculateCells({
							marginPercentage: 1,
						});
					}, 200);
				}
			} else {
				const scrollDiff = state.scrollPosition - prevScrollPosition.current;
				calculateCells({
					mode: "RenderOnly",
					// Rendering more aggressively to account for scroll
					marginPercentage: 0.1,
					topMargin: scrollDiff > 0 ? 0 : -4 * scrollDiff + 0.5 * state.windowHeight,
					bottomMargin: scrollDiff < 0 ? 0 : 4 * scrollDiff + 0.5 * state.windowHeight,
				});
				if (timeout.current == null) {
					timeout.current = setTimeout(() => {
						timeout.current = null;
						// Cleaning up extra off-screen renders
						if (store.getState().scrollPosition === prevScrollPosition.current) {
							// Also want to make sure that the things that should be rendered are rendered
							calculateCells({
								marginPercentage: 1,
							});
						} else {
							// Clean only here since we have a very wide margin percentage (think of this like garbage collection)
							calculateCells({
								mode: "CleanOnly",
								marginPercentage: 3,
							});
						}
					}, 200);
				}
			}
			prevScrollPosition.current = state.scrollPosition;
		});
		return () => {
			unsubscribe();
		};
	}, [store]);
};
export default useCalculateCells;
